import { SystemOfMeasurement } from "fond/types";
import { Currency } from "fond/types/currency";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { ScenarioConfiguration, VersionScenarioConfiguration } from "fond/types/version";
import { ExpectedFileMatch, fuzzyMatchFile } from "fond/utils/fuzzyFileMatch";
import { safeJSONParse } from "fond/utils/json";

import { c2sFileMap } from "./files";
import { FormData } from "./SetupForm";

export const ignoreLayerOption = {
  Type: "LAYER",
  ID: undefined,
  Key: undefined,
  Label: "Ignore Layer",
} as unknown as LayerConfig;

/**
 * File type identifiers for C2S artifacts
 */
export type C2SArtifactFileType = "layersConfiguration" | "costBreakdown" | "bom";

const KEYWORDS_BY_ID: Record<C2SArtifactFileType, string[]> = {
  bom: ["bom", "bill", "materials", "bill_of_materials", "billofmaterials"],
  costBreakdown: ["cost", "breakdown", "cost_breakdown", "costbreakdown"],
  layersConfiguration: ["layers", "configuration", "config", "layers_configuration", "layersconfiguration"],
};

const EXPECTED_FILES: Array<ExpectedFileMatch<C2SArtifactFileType>> = c2sFileMap.ArtifactFiles.map((fileConfig) => {
  const lastDotIndex = fileConfig.file.lastIndexOf(".");
  const id = fileConfig.id as C2SArtifactFileType;
  return {
    id,
    baseName: fileConfig.file.slice(0, lastDotIndex),
    keywords: KEYWORDS_BY_ID[id],
    allowedExtensions: [fileConfig.file.slice(lastDotIndex)],
  };
});

export const matchC2SArtifactFile = (uploadedFileName: string) => fuzzyMatchFile(uploadedFileName, EXPECTED_FILES);

const COST_PER_PASSING_AVERAGE_MULTIPLIER = 5;

export interface BudgetSliderConfig {
  max: number;
  defaultValue: number;
}

/**
 * Calculates the CFO budget maximums and default values.
 *
 * The default values are:
 * - The monetary budget is set to half the total cost, and
 * - The cost-per-passing budget is set to the average cost per passing.
 */
export const calculateBudgets = (
  bomTotal: number,
  serviceLocationCount: number | undefined
): { monetary: BudgetSliderConfig; costPerPassing: BudgetSliderConfig | undefined } => {
  const monetaryMax = Math.ceil(bomTotal);
  const average = serviceLocationCount ? bomTotal / serviceLocationCount : undefined;

  return {
    monetary: { max: monetaryMax, defaultValue: Math.round(monetaryMax / 2) },
    // There is no global upper bound on the allowable costPerPassing: allowing more budget than the average
    // cost-per-passing is reasonable _if_ the monetary budget is reduced. The COST_PER_PASSING_AVERAGE_MULTIPLIER here
    // is a fudge factor that is supposed to give the user a 'reasonable' range of choices.
    costPerPassing:
      average === undefined ? undefined : { max: Math.ceil(average * COST_PER_PASSING_AVERAGE_MULTIPLIER), defaultValue: Math.round(average) },
  };
};

export interface EffectiveBudget {
  value: number;
  limit: number;
}

/**
 * Calculate the effective budgets.
 *
 * The monetary and cost-per-passing budgets are interwoven and influence each other.
 */
export const calculateEffectiveBudgets = ({
  monetary,
  costPerPassing,
  isMonetaryBudgetEnabled,
  isCostPerPassingBudgetEnabled,
  monetaryBudget,
  costPerPassingBudget,
  serviceLocationCount,
}: {
  monetary: BudgetSliderConfig;
  costPerPassing: BudgetSliderConfig;
  isMonetaryBudgetEnabled: boolean;
  isCostPerPassingBudgetEnabled: boolean;
  monetaryBudget: number;
  costPerPassingBudget: number;
  serviceLocationCount: number;
}): { monetary: EffectiveBudget; costPerPassing: EffectiveBudget } => {
  // If the monetary budget is not enabled, then the cost-per-passing budget achieves nothing when it exceeds
  // the default value (the current average cost-per-passing).
  const costPerPassingLimit = isMonetaryBudgetEnabled && monetaryBudget < monetary.max ? costPerPassing.max : costPerPassing.defaultValue;
  const effectiveCostPerPassing = Math.min(costPerPassingBudget, costPerPassingLimit);

  // If a cost-per-passing budget is enabled, then the monetary budget is limited by the product
  // of this budget and the service location count.
  const monetaryLimit = isCostPerPassingBudgetEnabled
    ? Math.min(monetary.max, Math.ceil(effectiveCostPerPassing * serviceLocationCount))
    : monetary.max;

  return {
    monetary: { value: Math.min(monetaryBudget, monetaryLimit), limit: monetaryLimit },
    costPerPassing: { value: effectiveCostPerPassing, limit: costPerPassingLimit },
  };
};

/**
 * Return the step size to use for a slider with a given maximum value.
 *
 * The step size is chosen to give roughly 100 increments across the slider range.
 */
export const calculateSliderStep = (max: number): number => {
  const target = max / 100;
  if (target <= 1) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  return [5, 2, 1].map((factor) => factor * magnitude).find((step) => step <= target) ?? 1;
};

/**
 * Return whether the enabled budgets could theoretically restrict the build scope.
 */
export const isBudgetConfigRestrictive = ({
  bomTotal,
  averageCostPerPassing,
  monetaryBudget,
  costPerPassingBudget,
}: {
  bomTotal: number;
  averageCostPerPassing: number;
  monetaryBudget: number | null;
  costPerPassingBudget: number | null;
}): boolean =>
  (monetaryBudget !== null && monetaryBudget < bomTotal) || (costPerPassingBudget !== null && costPerPassingBudget < averageCostPerPassing);

export interface C2SLayersConfiguration {
  physical_elements?: string[] | null;
  network_elements?: string[] | null;
  edge_elements?: string[] | null;
  node_elements?: string[] | null;
  layer_name_mapping?: Record<string, string> | null;
  combine_layers?: Record<string, string[]> | null;
}

export const resolveLayerConfigurationNameFromFilename = (layersConfiguration: C2SLayersConfiguration, fileName: string): string => {
  return layersConfiguration?.layer_name_mapping?.[fileName] || fileName;
};

export const resolveLayerConfigurationInputLayers = (layersConfiguration: C2SLayersConfiguration): Set<string> => {
  let layers = new Set<string>();
  layersConfiguration?.physical_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.network_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.edge_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.node_elements?.forEach((el) => layers.add(el));

  // The BOM processes layer configurations by (i) renaming, and then (ii) combining layers. We're trying
  // to do the reverse here, so we first split up combined layers and then revert back to the original name.
  if (layersConfiguration?.combine_layers) {
    layers = new Set(Array.from(layers).flatMap((layer) => layersConfiguration?.combine_layers?.[layer] ?? [layer]));
  }

  if (layersConfiguration?.layer_name_mapping) {
    const reverseNameMap = new Map<string, string>(
      Object.entries(layersConfiguration.layer_name_mapping).map(([original, renamed]) => [renamed, original])
    );
    layers = new Set(Array.from(layers).map((layer) => reverseNameMap.get(layer) ?? layer));
  }

  return layers;
};

/**
 * Filters out null/undefined/ignoreLayerOption values from a layer mapping.
 */
export const filterLayerMappings = (mapping: Record<string, LayerConfig | null>): Record<string, LayerConfig> => {
  return Object.fromEntries(Object.entries(mapping).filter(([_, value]) => value != null && value !== ignoreLayerOption)) as Record<
    string,
    LayerConfig
  >;
};

export const convertLayerMappings = (mapping: Record<string, LayerConfig | null>): FormData["Inputs"]["ToolLayersConfiguration"]["Mappings"] => {
  return Object.entries(filterLayerMappings(mapping)).map(([key, { Key }]) => ({
    Name: key,
    Layer: { LayerKey: Key },
  }));
};

/**
 * Converts a VersionScenarioConfiguration to FormData format.
 * Specifically:
 * 1) Parses Inputs.ToolLayersConfiguration.LayersConfiguration.Data from JSON string to object
 * 2) Stringifies ToolConfig.CostToServe.AdditionalSettings from object to JSON string
 * 3) Stringifies ToolConfig.DesignPhasePartitioner.AdditionalSettings from object to JSON string
 * 4) Adds Files field for form compatibility
 */
export const convertConfigToFormData = (
  config: VersionScenarioConfiguration,
  systemOfMeasurement: SystemOfMeasurement = "imperial",
  currency: Currency = "USD"
): FormData => {
  return {
    Files: {
      Artifacts: [],
      Occupancy: null,
    },
    DefaultBins: config.ToolConfigurations.CostToServe.BinCount == null,
    Inputs: {
      ...config.Inputs,
      ToolLayersConfiguration: {
        ...config.Inputs.ToolLayersConfiguration,
        LayersConfiguration: {
          ...config.Inputs.ToolLayersConfiguration.LayersConfiguration,
          Data: safeJSONParse(config.Inputs.ToolLayersConfiguration.LayersConfiguration.Data || ""),
        },
      },
    },
    ToolConfigurations: {
      ...config.ToolConfigurations,
      CostToServe: {
        ...config.ToolConfigurations.CostToServe,
        AdditionalSettings: JSON.stringify(config.ToolConfigurations.CostToServe.AdditionalSettings),
      },
      DesignPhasePartitioner: {
        ...config.ToolConfigurations.DesignPhasePartitioner,
        AdditionalSettings: JSON.stringify(config.ToolConfigurations.DesignPhasePartitioner.AdditionalSettings),
      },
    },
    PathLayer: config.PathLayer,
    Currency: currency,
    SystemOfMeasurement: systemOfMeasurement,
  };
};

/**
 * Converts FormData to ScenarioConfiguration format.
 * Specifically:
 * 1) Stringifies Inputs.ToolLayersConfiguration.LayersConfiguration.Data from object to JSON string
 * 2) Parses ToolConfig.CostToServe.AdditionalSettings from JSON string to object
 * 3) Parses ToolConfig.DesignPhasePartitioner.AdditionalSettings from JSON string to object
 * 4) Removes Files field (not part of ScenarioConfiguration)
 */
export const convertFormDataToConfig = (formData: FormData): ScenarioConfiguration => {
  return {
    Inputs: {
      BillOfMaterials: {
        Type: "file",
        Data: formData.Inputs.BillOfMaterials.Data ?? "",
      },
      CostBreakdown: {
        Type: "file",
        Data: formData.Inputs.CostBreakdown.Data ?? "",
      },
      Occupancy:
        formData.Inputs.Occupancy.Type === "layer"
          ? {
              Type: "layer",
              Layer: {
                LayerKey: formData.Inputs.Occupancy.Layer?.LayerKey ?? "",
              },
            }
          : {
              Type: "file",
              Data: formData.Inputs.Occupancy.Data ?? "",
            },
      ToolLayersConfiguration: {
        ...formData.Inputs.ToolLayersConfiguration,
        LayersConfiguration: {
          ...formData.Inputs.ToolLayersConfiguration.LayersConfiguration,
          Data: JSON.stringify(formData.Inputs.ToolLayersConfiguration.LayersConfiguration.Data) ?? "",
        },
      },
    },
    ToolConfigurations: {
      CostToServe: {
        CostingLayer: formData.ToolConfigurations.CostToServe.CostingLayer ?? { LayerKey: "" },
        BinCount: formData.DefaultBins ? null : formData.ToolConfigurations.CostToServe.BinCount,
        AdditionalSettings: safeJSONParse(formData.ToolConfigurations.CostToServe.AdditionalSettings),
      },
      DesignPhasePartitioner: {
        ...formData.ToolConfigurations.DesignPhasePartitioner,
        AdditionalSettings: safeJSONParse(formData.ToolConfigurations.DesignPhasePartitioner.AdditionalSettings),
      },
    },
    PathLayer: formData.PathLayer?.LayerKey ? { LayerKey: formData.PathLayer.LayerKey } : null,
  };
};
